import {memo, useCallback, useMemo, useRef, useState} from 'react'
import {ModalTemplate} from '../template'
import {SmartA1BatchProcessing} from './SmartA1BatchProcessing'
import {SuggestSmartA1Batch, SuggestSmartA1BatchRef} from './SuggestSmartA1Batch'
import {CreateSmartA1Batch} from './CreateSmartA1Batch'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import A1BatchService from '../../core/_requests'
import {
  SmartA1Batch as TSmartA1Batch,
  SmartA1BatchesWithPoints,
  SuggestA1Batch,
} from '../../definitions'
import {toAbsoluteUrl} from '../../../../../_gori/helpers'
import {useIntl} from 'react-intl'

type SmartA1BatchProps = {
  show: boolean
  handleClose: () => void
  file?: any
  orderIds?: string[]
  handleError: (error, close?: boolean) => void
  handleCreateSuccess: (batchIds?: string[]) => void
}
export const SmartA1Batch = memo(
  ({show, handleClose, file, orderIds, handleError, handleCreateSuccess}: SmartA1BatchProps) => {
    const {newCancelToken: cancelToken1, isCancel: isCancel1} = useCancelToken()
    const {newCancelToken: cancelToken2, isCancel: isCancel2} = useCancelToken()
    const intl = useIntl()

    const suggestSmartA1BatchRef = useRef<SuggestSmartA1BatchRef>(null)

    const [mode, setMode] = useState<'confirmation' | 'processing' | 'suggested' | 'created'>(
      'confirmation'
    )
    const [progress, setProgress] = useState(0)
    const [suggestA1BatchData, setSuggestA1BatchData] = useState<SuggestA1Batch>({total: 0})
    const [smartA1BatchData, setSmartA1BatchData] = useState<SmartA1BatchesWithPoints>({
      batches: [],
      points: [],
    })
    const [loading, setLoading] = useState<{create: boolean}>({
      create: false,
    })

    const handleGenerate = useCallback(async () => {
      setProgress(0)
      const payload = new FormData()
      if (file) {
        payload.append('file', file)
        payload.append('type', 'file')
      } else {
        payload.append('order_ids', JSON.stringify(orderIds || []))
        payload.append('type', 'orders')
      }

      let progressInterval: NodeJS.Timeout | null = null

      const config = {
        headers: {'content-type': 'multipart/form-data'},
        cancelToken: cancelToken1(),
        onUploadProgress: (event: ProgressEvent) => {
          if (!event.total) return
          const percent = Math.round((event.loaded * 100) / event.total)
          setProgress(Math.min(percent, 60))

          if (percent >= 100 && !progressInterval) {
            let currentProgress = 60
            progressInterval = setInterval(() => {
              currentProgress += Math.random() * 8 + 2
              if (currentProgress >= 95) {
                currentProgress = 95
                if (progressInterval) clearInterval(progressInterval)
              }
              setProgress(Math.floor(currentProgress))
            }, 300)
          }
        },
      }
      try {
        const res = await A1BatchService.generateSmartA1Batch(payload, config)
        if (progressInterval) clearInterval(progressInterval)
        setSuggestA1BatchData(res)
        setProgress(100)
        setTimeout(() => setMode('suggested'), 200)
      } catch (error: any) {
        if (progressInterval) clearInterval(progressInterval)
        setProgress(100)
        if (isCancel1(error)) return
        handleError(error, true)
      }
    }, [file, orderIds, handleError, isCancel1, cancelToken1])

    const handleCreateSmartA1Batch = useCallback(
      async (data: SuggestA1Batch) => {
        setLoading((prev) => ({...prev, create: true}))
        try {
          const payload = new FormData()
          const warehouses =
            data?.warehouses?.map((w) => ({
              id: w.id,
              order_ids: w.orders.map((o) => o.id),
            })) || []
          if (file) {
            payload.append('file', file)
            payload.append('type', 'file')
          } else {
            payload.append('order_ids', JSON.stringify(orderIds || []))
            payload.append('type', 'orders')
          }

          payload.append('warehouses', JSON.stringify(warehouses))

          const config = {
            headers: {'content-type': 'multipart/form-data'},
            cancelToken: cancelToken2(),
          }
          const batches: TSmartA1Batch[] = await A1BatchService.createSmartA1Batch(payload, config)
          const points = suggestSmartA1BatchRef.current?.getPoints() || []

          setSmartA1BatchData({batches, points})
          setMode('created')

          const batchIds = batches.map((b) => b.batch.id)
          handleCreateSuccess(batchIds)
        } catch (error: any) {
          if (isCancel2(error)) return
          handleError(error)
        } finally {
          setLoading((prev) => ({...prev, create: false}))
        }
      },
      [file, orderIds, cancelToken2, handleCreateSuccess, isCancel2, handleError]
    )

    const modalProps = useMemo(
      () => ({
        confirmation: {
          showFooter: true,
          title: 'SMART_BATCHING_ADDRESS_CHANGE',
          titlePrimaryBtn: 'PROCEED',
          onClickPrimaryBtn: () => {
            setMode('processing')
            handleGenerate()
          },
        },
        processing: {
          showFooter: false,
        },
        suggested: {
          showFooter: true,
          titlePrimaryBtn: 'CREATE_A1_BATCHES',
          onClickPrimaryBtn: () => handleCreateSmartA1Batch(suggestA1BatchData),
          loadingPrimaryBtn: loading.create,
          disabledPrimaryBtn: !suggestA1BatchData.warehouses?.length,
        },
        created: {
          showFooter: true,
          title: 'SMART_A1_BATCH',
          titleSecondaryBtn: 'CLOSE',
          showPrimaryBtn: false,
        },
      }),
      [suggestA1BatchData, handleCreateSmartA1Batch, handleGenerate, loading.create]
    )

    return (
      <ModalTemplate
        id='gori_modal_smart_a1_batch'
        show={show}
        handleClose={handleClose}
        onClickSecondaryBtn={handleClose}
        {...modalProps[mode]}
      >
        {mode === 'confirmation' && (
          <div className='mx-10 my-5 d-flex flex-column justify-content-center align-items-center fs-4'>
            <div className='mb-5'>
              <img src={toAbsoluteUrl('/media/gori/dashboard/error_balance.svg')} alt='img' />
            </div>
            <p className='text-center'>
              {intl.formatMessage(
                {
                  id: 'A1_SMART_BATCHING_DESCRIPTION',
                },
                {
                  strong: (chunks) => <strong>{chunks}</strong>,
                }
              )}
            </p>
          </div>
        )}
        {mode === 'processing' && <SmartA1BatchProcessing progress={progress} />}
        {mode === 'suggested' && (
          <SuggestSmartA1Batch data={suggestA1BatchData} ref={suggestSmartA1BatchRef} />
        )}
        {mode === 'created' && <CreateSmartA1Batch data={smartA1BatchData} />}
      </ModalTemplate>
    )
  }
)
